import { useQueryClient } from "@tanstack/react-query"
import { isSameDay, isPast, differenceInHours } from "date-fns"

import { parseYearMonthDayHourMinute } from "@p-ban/common/src/utils/DateUtils"

import type { EstimateParams } from "../../../features/oneClickEstimate/types/params"
import type { EstimateResult } from "../csComEst.d"

const queryKey = "estimateApi.estimate"
const createCacheKey = (params: EstimateParams) => {
  return [queryKey, params] as const
}

const useEstimateCache = () => {
  const queryClient = useQueryClient()
  return {
    createCacheKey,
    removeExpiredCache(params: EstimateParams) {
      const cacheKey = createCacheKey(params)
      const query = queryClient.getQueryCache().find<unknown, unknown, EstimateResult>(cacheKey)
      if (!query) return

      const { dataUpdatedAt, data } = query.state
      if (!data || data.isError) return

      const totalResults = data.summaryTrialPurchaseResult.summaryTrialPurchaseTotalResults
      const isExpired = totalResults.some(
        (result) => !result.emptyResult && isPast(parseYearMonthDayHourMinute(result.orderLimit))
      )
      const now = new Date()

      if (isExpired || !isSameDay(now, dataUpdatedAt) || differenceInHours(now, dataUpdatedAt) >= 1) {
        queryClient.removeQueries(cacheKey)
      }
    },
    clear() {
      queryClient.removeQueries([queryKey])
    },
  }
}

export default useEstimateCache
